import {
  AccountName,
  ApiResponse,
  ExchangeToClientsMapping,
  FiltersData,
  FiltersSliceState,
  FilterType,
  FixMeLater,
  GetTradesDataResponse,
  GetTradesRequest,
  GridCardData,
  LanguageNameSpaces,
  LineAnnotation,
  MarginTypes,
  OpenMessage,
  OrderCardData,
  OrderStatus,
  OrderTypes,
  PrecisionConfig,
  RawLineChartDataPoint,
  SingleValueDataPoint,
  SocketTradeData,
  SortingOrder,
  TradeData,
  TradeExtraInfoActivity,
  TradeNextOrders,
  TradeOrderType,
  TradeStatus,
} from "@shared/types";
import {
  getExchangesToClientsMapping,
  getSingleExchangeToClientMapping,
  testRangeFilter,
  testStringListFilter,
  updateTradesFilters,
} from "@shared/features/filters/utils";
import {
  MARGIN_TYPE_TO_DISPLAY,
  TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME,
} from "@shared/common/constants";
import { get, minBy } from "lodash";
import {
  PORTFOLIO_DATA_CLIENT_HEADER,
  PORTFOLIO_DATA_EXCHANGE_HEADER,
} from "@shared/features/portfolio/constants";
import {
  addFloats,
  divideFloats,
  multiplyFloats,
  round,
  subtractFloats,
  toFixed,
} from "@shared/utils/numbers";
import {
  formatTimeSeriesChartData,
  getActivityColor,
  getLineAnnotation,
  getPointAnnotation,
} from "@shared/utils/charts";
import AppTheme, { AppThemeType } from "@shared/common/Themes/AppTheme";
import { Dispatch } from "@reduxjs/toolkit";
import {
  closedTradesFilters,
  detailsDrawerClosedTradesFilters,
  detailsDrawerOpenTradesFilters,
  openTradesFilters,
} from "@shared/slices/filters.slice";
import { sharedLib } from "@shared/services/shared.service";
import {
  ClosedTradeStatus,
  GET_TRADES_ARGS_KEY_TO_TRADE_DATA_KEY,
  OpenTradeStatus,
} from "@shared/features/trades/common/constants";
import {
  eval as evaluateExpression,
  parse as parseExpression,
} from "expression-eval";
import "nerdamer/Solve.js";
import nerdamer from "nerdamer";
import moment from "moment";
import {
  EditOrderData,
  TradeAmountData,
} from "@shared/features/trades/EditTrade/types";
import { EditTargetData } from "@shared/features/signals/EditSignal/types";
import {
  ErrorTypes,
  InputOrderData,
  OrdersErrors,
  ValidationOrdersData,
} from "@shared/features/trades/common/types";
import { sortByNumberProperty } from "@shared/utils/general";
import { TranslationKeys } from "@shared/common/i18n/types";
import {
  addError,
  getDefaultOrdersSectionErrors,
  validateNumberOfOrders,
} from "@shared/features/trades/common/validationUtils";
import { validateOrdersPrices } from "@shared/features/tradingTerminal/common/utils";

// TODO: need to consolidate with logic to preprocess portfolio data (from portfolio utils)
export const preprocessTradesData = (
  data: FixMeLater,
  headers: string[],
  exchangesToClientsMapping: ExchangeToClientsMapping
) => {
  const headersIndexToHeaderMapping = headers.reduce(
    (acc: FixMeLater, headerKey) => {
      acc[headers.indexOf(headerKey)] = headerKey;
      return acc;
    },
    {}
  );
  const tradesData: FixMeLater[] = [];
  Object.keys(exchangesToClientsMapping).forEach((exchangeName) => {
    get(exchangesToClientsMapping, [exchangeName], []).forEach(
      (clientName: AccountName) => {
        const rawClientTradesData = get(
          data,
          [exchangeName, clientName, "trade_cards"],
          []
        );
        const formattedData = rawClientTradesData.map(
          (tradeData: FixMeLater) => {
            const processedCoinData: FixMeLater = {
              [PORTFOLIO_DATA_EXCHANGE_HEADER]: exchangeName,
              [PORTFOLIO_DATA_CLIENT_HEADER]: clientName,
            };
            tradeData.forEach(
              (value: FixMeLater, index: number) =>
                (processedCoinData[headersIndexToHeaderMapping[index]] = value)
            );
            return processedCoinData;
          }
        );
        tradesData.push(...formattedData);
      }
    );
  });
  return tradesData;
};

export const filterTrades = (trades: TradeData[], filters: FiltersData) => {
  return trades?.filter((tradeData) => {
    return (
      testStringListFilter(
        filters[FilterType.CLIENTS]?.value,
        tradeData.account
      ) &&
      testStringListFilter(
        filters[FilterType.LONG_SHORT]?.value,
        tradeData["Long/Short"]
      ) &&
      testStringListFilter(
        filters[FilterType.MARGIN_TYPE]?.value,
        tradeData.margin_type || MARGIN_TYPE_TO_DISPLAY[MarginTypes.ISOLATED]
      ) &&
      testStringListFilter(
        filters[FilterType.TRADE_TYPE]?.value,
        tradeData.trade_type
      ) &&
      testRangeFilter(
        filters[FilterType.TIME_FRAME]?.value?.range,
        tradeData.timestamp
      ) &&
      testRangeFilter(
        filters[FilterType.LEVERAGE]?.value,
        tradeData.leverage || 1
      ) &&
      testStringListFilter(
        filters[FilterType.SOURCE_NAME]?.value,
        tradeData.group
      ) &&
      (!filters[FilterType.IS_HOT]?.value || tradeData.closest_order?.is_hot)
    );
  });
};

export const getAnnotationColor = (
  orderType: OrderTypes,
  appTheme: AppThemeType
) => {
  const orderTypeToColor = {
    [OrderTypes.ENTRY]: appTheme.colors.green,
    [OrderTypes.TAKE_PROFIT]: appTheme.colors.red,
    [OrderTypes.STOP]: appTheme.colors.orange,
  };
  return orderTypeToColor[orderType];
};

export const getTradesFilterTypeToTitle = (
  isOpen: boolean
): { [key in FilterType]?: TranslationKeys<LanguageNameSpaces.COMMON> } => ({
  [FilterType.TIME_FRAME]: isOpen
    ? "filters.titles.trade_open_time"
    : "filters.titles.trade_close_time",
});

export const getAnnotationDistanceData = (
  price: number,
  currentPrice: number | undefined,
  _pricePrecision?: number,
  distance?: number
) => {
  let distanceText: string | undefined;
  let localDistance: number | undefined = distance;
  if (!localDistance && currentPrice) {
    localDistance = ((currentPrice - price) / currentPrice) * -100;
  }
  if (localDistance !== undefined) {
    localDistance = toFixed(localDistance, 2);
    distanceText = `${localDistance > 0 ? "+" : ""}${localDistance}%`;
  }

  return {
    distance: localDistance,
    distanceText,
  };
};

const findLastIndex = (array: number[], maxPrice: number) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i] === maxPrice) {
      return i;
    }
  }
};

const getBorderOrdersIndex = (ordersPrices: number[]) => {
  const minPrice = Math.min(...ordersPrices);
  const maxPrice = Math.max(...ordersPrices);
  const minOrderIndex = ordersPrices.findIndex(
    (orderPrice) => orderPrice === minPrice
  );
  const maxOrderIndex = findLastIndex(ordersPrices, maxPrice);
  if (minPrice === maxPrice) return [maxOrderIndex];
  return [minOrderIndex, maxOrderIndex];
};

export const getOrdersAnnotation = (
  ordersPrices: { price: number; orderNumber?: number }[],
  color: string,
  precisionConfig?: PrecisionConfig,
  currentPrice?: number,
  distance?: number,
  orderType?: OrderTypes,
  text?: string,
  precisionNumber?: number
) => {
  const prices = ordersPrices.map((order) => order.price);
  const borderOrdersIndex = getBorderOrdersIndex(prices);
  const precision =
    precisionNumber || precisionConfig?.price || precisionConfig?.default || 0;
  return (
    ordersPrices?.map((order, index) => {
      const { distance: parsedDistance, distanceText } =
        getAnnotationDistanceData(
          order.price,
          currentPrice,
          precision,
          distance
        );
      const yValue = round(order.price, precision);
      return getLineAnnotation({
        yMax: yValue,
        color,
        distance: distanceText,
        showDistanceAbove: parsedDistance ? parsedDistance > 0 : false,
        orderType,
        orderNumber: order.orderNumber ?? index + 1,
        isSimpleLine: !borderOrdersIndex.includes(index),
        text,
      });
    }) || []
  );
};

export const getBotActiveTradeClosestOrdersAnnotations = (
  tradeNextOrders: TradeNextOrders | undefined,
  appTheme: typeof AppTheme,
  precision?: PrecisionConfig,
  socketPrice?: number,
  pricePrecision?: number
) => {
  const annotations: LineAnnotation[] = [];
  const { next_entry, next_stop, next_take_profit } = tradeNextOrders || {};
  const nextEntryOrStopOrder = next_entry || next_stop;
  if (nextEntryOrStopOrder) {
    const orderType = !!next_entry ? OrderTypes.ENTRY : OrderTypes.STOP;
    const distance = socketPrice
      ? -((socketPrice - nextEntryOrStopOrder.price) / socketPrice)
      : nextEntryOrStopOrder.distance;
    annotations.push(
      getOrdersAnnotation(
        [{ price: nextEntryOrStopOrder.price }],
        getAnnotationColor(orderType, appTheme),
        precision,
        undefined,
        distance * 100,
        orderType,
        `Next ${TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME[orderType]}`,
        pricePrecision
      )[0]
    );
  }
  if (next_take_profit) {
    const distance = socketPrice
      ? -((socketPrice - next_take_profit.price) / socketPrice)
      : next_take_profit.distance;
    annotations.push(
      getOrdersAnnotation(
        [{ price: next_take_profit.price }],
        getAnnotationColor(OrderTypes.TAKE_PROFIT, appTheme),
        precision,
        undefined,
        distance * 100,
        OrderTypes.TAKE_PROFIT,
        `Next ${TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME[OrderTypes.TAKE_PROFIT]}`,
        pricePrecision
      )[0]
    );
  }
  return annotations;
};

const getOrderPrice = (
  order: EditOrderData | OrderCardData | GridCardData | EditTargetData
) => {
  if ("edit_target_price" in order) return order.edit_target_price;
  else if ("target_price" in order) return order.target_price;
  return order.price;
};

export const getTradeClosestOrder = (
  ordersData:
    | (EditOrderData | OrderCardData | GridCardData | EditTargetData)[]
    | undefined,
  currentPrice?: number
) => {
  if (currentPrice !== undefined) {
    return minBy(ordersData, (order) =>
      Math.abs(Number(getOrderPrice(order)) - currentPrice)
    );
  }
  return minBy(ordersData, (order) => order.number);
};

const getClosestOrderText = (orderType: OrderTypes, orderNumber?: number) =>
  `Next ${TV_CHART_ORDER_TYPE_TO_DISPLAY_NAME[orderType]}${
    orderNumber !== undefined ? ` (#${orderNumber})` : ""
  }`;

export const getClosestOrderAnnotation = (
  order: EditOrderData | OrderCardData | GridCardData | EditTargetData,
  orderType: OrderTypes,
  color: string,
  pricePrecision?: number,
  currentPrice?: number
) => {
  const orderPrice = getOrderPrice(order);
  const { distanceText } = getAnnotationDistanceData(
    Number(orderPrice),
    currentPrice,
    pricePrecision || 0
  );
  const yValue = round(orderPrice, pricePrecision);
  return getLineAnnotation({
    yMax: yValue,
    color,
    distance: distanceText,
    orderType,
    text: getClosestOrderText(orderType, order.number),
  });
};

const updatedGetOrdersAnnotation = (
  theme: AppThemeType,
  orders: (EditOrderData | OrderCardData | GridCardData)[],
  orderType: OrderTypes,
  currentPrice?: number,
  pricePrecision?: number,
  onlyShowPending = true
) => {
  const color = getAnnotationColor(orderType, theme);
  const filteredOrders = onlyShowPending
    ? orders.filter(
        (order) => "status" in order && order.status.includes("Pending")
      )
    : orders;

  const closestOrder = getTradeClosestOrder(filteredOrders, currentPrice);
  const closestOrderAnnotation = closestOrder
    ? getClosestOrderAnnotation(
        closestOrder,
        orderType,
        color,
        pricePrecision,
        currentPrice
      )
    : undefined;

  return [closestOrderAnnotation].concat(
    filteredOrders
      .filter((order) => order.id !== closestOrder?.id)
      .map((order) => {
        return getLineAnnotation({
          yMax: round(getOrderPrice(order), pricePrecision),
          color,
          orderType,
          isSimpleLine: true,
        });
      })
  );
};

export const getTradeAnnotations = (
  theme: FixMeLater,
  entryOrders: (EditOrderData | OrderCardData | GridCardData)[] | undefined,
  takeProfitOrders:
    | (EditOrderData | OrderCardData | GridCardData)[]
    | undefined,
  stopOrders: (EditOrderData | OrderCardData | GridCardData)[] | undefined,
  pricePrecision: number,
  onlyShowPending = true,
  currentPrice?: number
) => {
  const orderTypeToOrders = {
    [OrderTypes.ENTRY]: entryOrders,
    [OrderTypes.TAKE_PROFIT]: takeProfitOrders,
    [OrderTypes.STOP]: stopOrders,
  };
  return [OrderTypes.ENTRY, OrderTypes.TAKE_PROFIT, OrderTypes.STOP]
    .map((orderType) => {
      let orders = orderTypeToOrders[orderType];
      if (!orders) return [];
      orders = orders.map((order, index) =>
        order.number === -1 ? { ...order, number: index + 1 } : order
      );
      return updatedGetOrdersAnnotation(
        theme,
        orders,
        orderType,
        currentPrice,
        pricePrecision,
        onlyShowPending
      );
    })
    .flat()
    .filter((annotation) => !!annotation)
    .sort((a, b) =>
      a && b ? +(b.isSimpleLine || false) - +(a.isSimpleLine || false) : 0
    ) as LineAnnotation[];
};

export const getTradesTransformResponse = (
  response: ApiResponse<GetTradesDataResponse>,
  args: GetTradesRequest | void,
  isOpenTrades?: boolean
) => {
  let tradesResponse = {
    filters: response.data.filters,
    precision: response.data.precision,
    info_link: response.data.info_link,
    aggregated_data: response.data.aggregated_data,
  };
  let exchangesToClientsMapping: Record<string, string[] | undefined>;
  if (args?.account_id)
    exchangesToClientsMapping = getSingleExchangeToClientMapping(
      response.data.data
    );
  else if (args?.exchange)
    exchangesToClientsMapping = {
      [args?.exchange]: response.data.filters?.values?.["Accounts"] || [],
    };
  else
    exchangesToClientsMapping = getExchangesToClientsMapping(
      response.data.filters
    );
  const formattedData = preprocessTradesData(
    response.data.data,
    response.data.headers || [],
    exchangesToClientsMapping
  );
  if (isOpenTrades) {
    let filtersSlice = openTradesFilters;
    if (
      args?.coin ||
      args?.account_id ||
      args?.exchange ||
      args?.dca_bot_id ||
      args?.signal_id
    )
      filtersSlice = detailsDrawerOpenTradesFilters;
    const { dispatch } = sharedLib.store;
    dispatch(filtersSlice.actions.loadFilters({ data: response.data, args }));
  }
  return { ...tradesResponse, data: formattedData };
};

export const getDistanceFromCurrentPrice = (
  currentPrice: number,
  price: number
) => {
  const distance = -((currentPrice - price) / currentPrice);
  return toFixed(distance * 100, 2);
};

const validateTradeDataToArgs = (
  updatedTrade: SocketTradeData,
  args: GetTradesRequest | undefined
) => {
  if (!args) return true;

  const isInvalid = Object.entries(GET_TRADES_ARGS_KEY_TO_TRADE_DATA_KEY).some(
    ([argsKey, tradeKey]) =>
      argsKey in args &&
      args[argsKey as keyof GetTradesRequest] !==
        updatedTrade[tradeKey as keyof SocketTradeData]
  );

  return !isInvalid;
};

export const updateOpenTradesData = (
  args: GetTradesRequest | undefined,
  updatedTrades: SocketTradeData | SocketTradeData[] | undefined,
  dispatch: Dispatch<any>
) => {
  const updatedTradesArray =
    Array.isArray(updatedTrades) || !updatedTrades
      ? updatedTrades
      : [updatedTrades];
  return sharedLib.sharedApi.trades.util.updateQueryData(
    "getOpenTradesData",
    args,
    (openTrades) => {
      const openTradesData = openTrades?.data;
      if (!openTradesData || !updatedTradesArray) return;
      updatedTradesArray.forEach((updatedTrade) => {
        const isValid = validateTradeDataToArgs(updatedTrade, args);
        if (isValid) {
          const tradeIndex = openTradesData.findIndex(
            (trade: TradeData) => trade.id === updatedTrade.id
          );
          if (
            updatedTrade.status_value &&
            OpenTradeStatus.has(updatedTrade.status_value)
          ) {
            if (tradeIndex === -1) openTradesData.push(updatedTrade);
            else openTradesData[tradeIndex] = updatedTrade;
            dispatch(
              openTradesFilters.actions.updateFilters({
                filtersUpdateFunction: (state: FiltersSliceState) =>
                  updateTradesFilters(state, updatedTrade),
              })
            );
          } else if (tradeIndex >= 0) {
            openTradesData.splice(tradeIndex, 1);
          }
        }
      });
    }
  );
};

export const updateClosedTradesData = (
  args: GetTradesRequest | undefined,
  updatedTrades: SocketTradeData | SocketTradeData[] | undefined,
  dispatch: Dispatch<any>
) => {
  const updatedTradesArray =
    Array.isArray(updatedTrades) || !updatedTrades
      ? updatedTrades
      : [updatedTrades];
  return sharedLib.sharedApi.trades.util.updateQueryData(
    "getClosedTradesData",
    args,
    (closedTrades) => {
      const closedTradesData = closedTrades?.data;
      if (!closedTradesData || !updatedTradesArray) return;
      updatedTradesArray.forEach((updatedTrade) => {
        const isValid = validateTradeDataToArgs(updatedTrade, args);
        if (isValid) {
          const tradeIndex = closedTradesData.findIndex(
            (trade: TradeData) => trade.id === updatedTrade.id
          );
          if (
            updatedTrade.status_value &&
            ClosedTradeStatus.has(updatedTrade.status_value) &&
            tradeIndex === -1
          ) {
            closedTradesData.push(updatedTrade);
            dispatch(
              closedTradesFilters.actions.updateFilters({
                filtersUpdateFunction: (state: FiltersSliceState) =>
                  updateTradesFilters(state, updatedTrade),
              })
            );
          }
        }
      });
    }
  );
};

export const getOrdersDefaultSortOrder = (
  orderType: OrderTypes,
  isShort: boolean,
  isBreakout: boolean
): SortingOrder => {
  // Entry - !short !breakout desc, short !breakout - asc, !short breakout - asc, short breakout - desc
  // TakeProfit - !short !breakout asc, short !breakout - desc, !short breakout - asc, short breakout - desc
  let sortOrder = SortingOrder.DESCEND;
  if (orderType === OrderTypes.ENTRY) {
    if ((isShort && !isBreakout) || (!isShort && isBreakout)) {
      sortOrder = SortingOrder.ASCEND;
    }
  } else if (orderType === OrderTypes.TAKE_PROFIT) {
    if ((!isShort && !isBreakout) || (!isShort && isBreakout)) {
      sortOrder = SortingOrder.ASCEND;
    }
  }
  return sortOrder;
};

export const getErrorDisplayMessage = (
  t: (key: TranslationKeys<LanguageNameSpaces.TRADES>, options?: any) => string,
  errorType: ErrorTypes,
  orderType?: OrderTypes,
  isSignal?: boolean
): string => {
  let errorTypeToTranslationKey: {
    [key in ErrorTypes]?: TranslationKeys<LanguageNameSpaces.TRADES>;
  } = {
    [ErrorTypes.DUPLICATE_PRICE]: "validation.duplicate_prices",
    [ErrorTypes.LONG_ENTRY_ABOVE_TAKE_PROFIT]: "validation.entry_below_tp_long",
    [ErrorTypes.SHORT_ENTRY_BELOW_TAKE_PROFIT]:
      "validation.entry_above_tp_short",
    [ErrorTypes.LONG_TAKE_PROFIT_BELOW_ENTRIES]:
      "validation.tp_above_entry_long",
    [ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_ENTRIES]:
      "validation.tp_below_entry_short",
    [ErrorTypes.LONG_STOP_ABOVE_ENTRIES]: "validation.stop_below_entry_long",
    [ErrorTypes.SHORT_STOP_BELOW_ENTRIES]: "validation.stop_above_entry_short",
    [ErrorTypes.LONG_STOP_ABOVE_CURRENT_PRICE]:
      "validation.stop_below_current_long",
    [ErrorTypes.SHORT_STOP_BELOW_CURRENT_PRICE]:
      "validation.stop_above_current_short",
    [ErrorTypes.LONG_BREAKOUT_STOP_ABOVE_CURRENT_PRICE]:
      "validation.cancel_stop_below_current_long",
    [ErrorTypes.SHORT_BREAKOUT_STOP_BELOW_CURRENT_PRICE]:
      "validation.cancel_stop_above_current_short",
    [ErrorTypes.LONG_BREAKOUT_ENTRY_BELOW_CURRENT_PRICE]:
      "validation.breakout_long_entry",
    [ErrorTypes.SHORT_BREAKOUT_ENTRY_ABOVE_CURRENT_PRICE]:
      "validation.breakout_short_entry",
    [ErrorTypes.BREAKOUT_TOO_MANY_ENTRIES]: "validation.max_breakout_entries",
    [ErrorTypes.INVALID_ENTRY_ORDER_AMOUNT]: "validation.invalid_amounts",
    [ErrorTypes.INVALID_ORDER_RATIO]: "validation.invalid_ratios",
    [ErrorTypes.INVALID_ORDER_PRICE]: "validation.invalid_prices",
    [ErrorTypes.EDIT_TRADE_SUM_OF_RATIOS]: "validation.tp_sum_exceeds",
    [ErrorTypes.AT_LEAST_ONE_ENTRY]: "validation.at_least_one_entry",
    [ErrorTypes.INVALID_NUMBER_OF_TAKE_PROFIT_AND_STOP]:
      "validation.existing_quantity_requires_targets",
    [ErrorTypes.INVALID_TRADE_QUANTITY]: "validation.invalid_existing_quantity",
    [ErrorTypes.RATIO_MUST_BE_SPECIFIED]:
      "validation.partial_ratios_not_allowed",
    [ErrorTypes.SUM_OF_RATIOS]: "validation.ratio_sum_must_be_100",
    [ErrorTypes.CANNOT_BREAK_EVEN_UNFULFILLED_TARGETS]:
      "validation.breakeven_no_fulfilled",
    [ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_STOP]: "validation.tp_below_stop_short",
    [ErrorTypes.LONG_TAKE_PROFIT_BELOW_STOP]: "validation.no_pending_targets",
    [ErrorTypes.CANT_REDISTRIBUTE_NO_SIBLINGS_TARGETS]:
      "validation.no_pending_targets",
    [ErrorTypes.CANT_REDISTRIBUTE_NO_SIBLINGS_NON_FULFILLED_TARGETS]:
      "validation.no_pending_unfulfilled_targets",
    [ErrorTypes.LONG_TAKE_PROFIT_BELOW_CURRENT_PRICE]:
      "validation.cancel_stop_below_current_long",
    [ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_CURRENT_PRICE]:
      "validation.cancel_stop_above_current_short",
  };
  const errorTypeToDisplayMessage = Object.fromEntries(
    Object.entries(errorTypeToTranslationKey).map(([type, text]) => [
      type,
      t(text, {
        item: isSignal ? t("labels.signal") : t("labels.trade"),
      }),
    ])
  );

  const orderTypeToDisplay = {
    [OrderTypes.ENTRY]: t("orders.entry"),
    [OrderTypes.TAKE_PROFIT]: t("orders.take_profit"),
    [OrderTypes.STOP]: t("orders.stop"),
  };
  if (errorType in errorTypeToDisplayMessage) {
    return errorTypeToDisplayMessage[errorType];
  } else if (
    errorType === ErrorTypes.SUM_OF_RATIOS &&
    orderType !== undefined
  ) {
    return t("validation.sum_orders", {
      orderType: orderTypeToDisplay[orderType],
    });
  }
  return t("validation.generic_error");
};

export const getStopCurrentPriceValidation = (
  stopPrice: number,
  currentPrice: number,
  reverse: boolean,
  isBreakout?: boolean,
  cancelOnFirstTargetReached?: boolean
) => {
  if (isBreakout && !cancelOnFirstTargetReached) return;
  if (reverse && stopPrice < currentPrice) {
    return isBreakout
      ? ErrorTypes.SHORT_BREAKOUT_STOP_BELOW_CURRENT_PRICE
      : ErrorTypes.SHORT_STOP_BELOW_CURRENT_PRICE;
  } else if (!reverse && stopPrice > currentPrice) {
    return isBreakout
      ? ErrorTypes.LONG_BREAKOUT_STOP_ABOVE_CURRENT_PRICE
      : ErrorTypes.LONG_STOP_ABOVE_CURRENT_PRICE;
  }
};

export const getNormalizer = (
  percentages: number[],
  normalizeTo: number = 100
) => {
  const totalPercentage = percentages.reduce((a, b) => addFloats(a, b), 0);
  return divideFloats(normalizeTo, totalPercentage || 1);
};

export const getNormalizedPercentages = (
  percentages: number[],
  percentPrecision: number,
  normalizeTo = 100,
  addReminder = true
) => {
  const normalizer = getNormalizer(percentages, normalizeTo);
  const normalizedPercentages = percentages.map((percent) =>
    multiplyFloats(normalizer, percent)
  );
  if (normalizedPercentages.length && addReminder) {
    const percentSum = normalizedPercentages.reduce(
      (sum, percent) => addFloats(sum, toFixed(percent, percentPrecision)),
      0
    );
    const reminder = subtractFloats(normalizeTo, percentSum);
    normalizedPercentages[0] = addFloats(normalizedPercentages[0], reminder);
  }
  return normalizedPercentages;
};

export const convertAmountToQuantity = (
  amount: number,
  leverageValue: number,
  currentPrice: number,
  quantityConversionFormula?: string
) => {
  if (!quantityConversionFormula) return;
  const ast = parseExpression(quantityConversionFormula);
  const variables = {
    leverage: leverageValue.toString(),
    order_price: currentPrice.toString(),
    amount: amount.toString(),
  };

  return evaluateExpression(ast, variables);
};

export const solveNerdamer = (
  equation: string,
  variables: Record<string, string>,
  solveFor: string
) => {
  try {
    const eq = nerdamer(equation, variables).evaluate();
    let result = eq.solveFor(solveFor);
    if (Array.isArray(result)) {
      result = result[0];
    }
    return result.text();
  } catch (error) {
    return "0";
  }
};

export const getNerdamerConversionFormulaValue = (
  additionalVariables: Record<string, string>,
  leverageValue: number,
  currentPrice: number,
  solveFor: string,
  conversionFormula: string
) => {
  const variables = {
    leverage: leverageValue.toString(),
    order_price: currentPrice.toString(),
    ...additionalVariables,
  };
  const result = solveNerdamer(
    "amount=" + conversionFormula,
    variables,
    solveFor
  );
  return parseFloat(result);
};

export const convertQuantityToAmount = (
  quantity: number,
  leverageValue: number,
  currentPrice: number,
  amountConversionFormula?: string
) => {
  if (!amountConversionFormula) return;
  const ast = parseExpression(amountConversionFormula);
  const variables = {
    leverage: leverageValue.toString(),
    order_price: currentPrice.toString(),
    quantity: quantity.toString(),
  };
  return evaluateExpression(ast, variables);
};

export const getConversionMinCost = (
  isEntry: boolean,
  leverageValue: number,
  amountPrecision: number | undefined,
  quantityPrecision: number | undefined,
  price: number,
  conversionFormula?: string,
  minAmount?: number,
  minQuantity?: number,
  quantityConversionFormula?: string
) => {
  const minAmountExists = typeof minAmount === "number";
  const minQuantityExists = typeof minQuantity === "number";
  if ((!minAmountExists && !minQuantityExists) || !conversionFormula) return 0;
  let conversionMinAmount: number | null = null;
  let conversionMinQuantity: number | null = null;
  let precisedMinAmount: number | null = null;
  if (minAmountExists) {
    conversionMinQuantity = quantityConversionFormula
      ? convertAmountToQuantity(minAmount, 1, price, quantityConversionFormula)
      : getNerdamerConversionFormulaValue(
          { amount: minAmount.toString() },
          1,
          price,
          "quantity",
          conversionFormula
        );
    conversionMinQuantity = toFixed(
      conversionMinQuantity,
      quantityPrecision,
      true
    );
    precisedMinAmount = convertQuantityToAmount(
      conversionMinQuantity,
      leverageValue,
      price,
      conversionFormula
    );
  }
  if (minQuantityExists) {
    conversionMinAmount = convertQuantityToAmount(
      minQuantity,
      leverageValue,
      price,
      conversionFormula
    );
  }
  const minAmounts = [conversionMinAmount || 0, precisedMinAmount || 0];
  const precisedFinalAmount = toFixed(
    Math.max(...minAmounts),
    amountPrecision,
    true
  );
  if (isEntry) return precisedFinalAmount;
  const convertedFinalQuantity = getNerdamerConversionFormulaValue(
    { amount: precisedFinalAmount.toString() },
    leverageValue,
    price,
    "quantity",
    conversionFormula
  );
  return toFixed(convertedFinalQuantity, quantityPrecision);
};

export const getActivitiesAnnotations = (
  activities: TradeExtraInfoActivity[],
  theme: AppThemeType
) => {
  return activities
    ?.map((activity) => {
      const { color } = getActivityColor(activity.activity_component, theme);
      const xValue = moment
        .unix(activity.relevant_time)
        .format("YYYY/MM/DD HH:mm:ss");
      const yValue = activity.execution_price;
      return getPointAnnotation(xValue, yValue, color, activity.relevant_time);
    })
    .filter((annotation) => annotation.yValue);
};

export const redistributeOrdersWithAddedRatio = <
  T extends EditOrderData | EditTargetData
>(
  takeProfitOrders: T[],
  addedRatio: number,
  ignoreIndex?: number
): T[] => {
  const getOrderRatio = (order: EditOrderData | EditTargetData) =>
    order.edit_ratio || 0;
  const getOrderFilledQuantity = (order: EditOrderData | EditTargetData) =>
    ("filled_quantity" in order && order.filled_quantity) || 0;
  const totalRatios = takeProfitOrders
    .filter((order, index) => {
      return (
        ("edit_status" in order ? order.edit_status : order.status) ===
          OrderStatus.PENDING &&
        index !== ignoreIndex &&
        !getOrderFilledQuantity(order)
      );
    })
    .reduce((sum, order) => addFloats(sum, getOrderRatio(order)), 0);

  return takeProfitOrders.map((order, index) => {
    const orderRatio = getOrderRatio(order);
    const multiplier = divideFloats(orderRatio, totalRatios);
    const newOrderRatio = addFloats(
      orderRatio,
      multiplyFloats(addedRatio, multiplier)
    );
    return order.status === OrderStatus.PENDING &&
      index !== ignoreIndex &&
      !getOrderFilledQuantity(order)
      ? {
          ...order,
          edit_ratio: newOrderRatio,
        }
      : order;
  });
};

const getNormalizedValues = (values: number[], normalizeTo: number = 100) => {
  const normalizer = getNormalizer(values, normalizeTo);
  return values.map((value) => normalizer * value);
};

export const getEditWeightedAverageEntryPrice = (
  entries: (EditOrderData | EditTargetData)[]
) => {
  const normalizedPercentages = getNormalizedValues(
    entries.map((orderData) => orderData.edit_ratio || 1),
    1
  );
  return entries.reduce((average, order, index) => {
    const price =
      typeof order.edit_target_price === "string"
        ? parseFloat(order.edit_target_price)
        : order.edit_target_price;
    average += price * normalizedPercentages[index];
    return average;
  }, 0);
};

export const getWeightedAverageEntryPrice = (entries: InputOrderData[]) => {
  const normalizedPercentages = getNormalizedValues(
    entries.map((orderData) => parseFloat(orderData.percent))
  );
  return entries.reduce((average, order, index) => {
    average += (parseFloat(order.price) * normalizedPercentages[index]) / 100;
    return average;
  }, 0);
};

export const orderHasStatus = (
  order:
    | { status?: OrderStatus }
    | { status: OrderStatus; edit_status: OrderStatus },
  checkEditStatus?: boolean,
  ...statusListToCheck: OrderStatus[]
) => {
  const checkStatus = (
    status: OrderStatus | undefined,
    statusList: OrderStatus[]
  ) => {
    if (!status) return statusList.includes(OrderStatus.PENDING);
    return statusList
      .map((s) => s.toLowerCase())
      .includes(status?.toLowerCase());
  };

  if (checkEditStatus && !("edit_status" in order))
    throw new Error("order does not have edit_status property");

  if (checkEditStatus)
    return checkStatus(
      (order as { edit_status: OrderStatus }).edit_status,
      statusListToCheck
    );

  return checkStatus(order.status, statusListToCheck);
};

export const getPriceGraphDataWithActivities = (
  graphData?: RawLineChartDataPoint[],
  activities?: TradeExtraInfoActivity[],
  precisionConfig?: PrecisionConfig
) => {
  if (!precisionConfig || !graphData?.length) return [];
  const precision = precisionConfig.price || precisionConfig.default || 0;
  const activitiesDataPoints: SingleValueDataPoint[] =
    activities
      ?.filter((activityData) => {
        return (
          activityData.relevant_time > graphData[0][0] &&
          activityData.execution_price
        );
      })
      .map((activityData) => [
        activityData.relevant_time,
        activityData.execution_price,
      ]) || [];
  const chartDataWithActivities = [...graphData, ...activitiesDataPoints];
  return formatTimeSeriesChartData(
    chartDataWithActivities,
    1,
    true,
    null,
    precision
  );
};

export const getSplitActiveAndWaitingCancellationTrades = (
  trades?: TradeData[]
) => {
  let activeTrades: TradeData[] | undefined;
  let waitingCancellationTrades: TradeData[] | undefined;
  if (trades) {
    activeTrades = [];
    waitingCancellationTrades = [];
    trades?.forEach((trade) => {
      if (trade.status_value === TradeStatus.IN_CANCEL)
        waitingCancellationTrades?.push(trade);
      else activeTrades?.push(trade);
    });
  }
  return [activeTrades, waitingCancellationTrades];
};

export const getTradesFiltersSlice = (
  isOpenTrades: boolean,
  inDrawer: boolean
) => {
  if (isOpenTrades) {
    if (inDrawer) return detailsDrawerOpenTradesFilters;
    return openTradesFilters;
  }
  if (inDrawer) return detailsDrawerClosedTradesFilters;
  return closedTradesFilters;
};

export const getTradeFulfilledAmount = (
  entries: OrderCardData[],
  precision: number
) => {
  return round(
    entries.reduce((acc, order) => {
      acc += order.filled_amount;
      return acc;
    }, 0),
    precision
  );
};

export const getTradeTotalAmount = (
  entries: OrderCardData[],
  precision?: number
) => {
  const rawValue = entries
    .filter((order) => order.status !== OrderStatus.CANCELLED)
    .reduce((acc, order) => {
      return addFloats(acc, order.amount);
    }, 0);
  if (precision === undefined) return rawValue;
  return round(rawValue, precision);
};

export const isTakeProfitOrderType = (orderType?: TradeOrderType) => {
  return (
    !!orderType &&
    [
      TradeOrderType.SELL_STOP,
      TradeOrderType.SELL_LIMIT,
      TradeOrderType.SELL_MARKET,
    ].includes(orderType)
  );
};

export const isEntryOrderType = (orderType?: TradeOrderType) => {
  return (
    !!orderType &&
    [
      TradeOrderType.BUY_STOP,
      TradeOrderType.BUY_LIMIT,
      TradeOrderType.BUY_MARKET,
    ].includes(orderType)
  );
};
export const getValidationMessage = (
  fieldName: string,
  minValue?: number,
  maxValue?: number
) => {
  let validationString = "";
  if (minValue && maxValue) {
    validationString = `between ${minValue} and ${maxValue}`;
  } else if (minValue) {
    validationString = `above ${minValue}`;
  } else if (maxValue) {
    validationString = `below ${maxValue}`;
  }
  return `${fieldName} should be ${validationString}`;
};

export const getAmountValidationMessage = (
  minAmount?: number,
  maxAmount?: number
) => getValidationMessage("Amount", minAmount, maxAmount);

export const updateEditOrdersWithAddedAmount = (
  orders: EditOrderData[],
  amountToAdd: number,
  amountPrecision: number,
  quantityPrecision: number,
  isShort: boolean,
  isBreakout: boolean
): EditOrderData[] => {
  const normalizer = getNormalizer(
    orders
      .filter((order) => order.status === OrderStatus.PENDING)
      .map((order) => order.edit_ratio),
    1
  );

  let totalAmountAdded = 0;
  const updatedOrders = orders.map((order) => {
    if (order.status !== OrderStatus.PENDING) return order;
    const orderAmountToAdd = toFixed(
      order.edit_ratio * amountToAdd * normalizer,
      amountPrecision
    );
    if (!orderAmountToAdd) return order;
    totalAmountAdded = addFloats(totalAmountAdded, orderAmountToAdd);
    const newOrderAmount = addFloats(order.edit_amount, orderAmountToAdd);
    return {
      ...order,
      edit_amount: newOrderAmount,
      edit_quantity: toFixed(
        (newOrderAmount / order.edit_amount) * order.edit_quantity,
        quantityPrecision
      ),
    };
  });

  const leftoverAmount = subtractFloats(amountToAdd, totalAmountAdded);
  if (leftoverAmount) {
    const sortOrder = getOrdersDefaultSortOrder(
      OrderTypes.ENTRY,
      isShort,
      isBreakout
    );
    const orderToUpdate = updatedOrders
      .filter((order) => order.status === OrderStatus.PENDING)
      .sort((a, b) =>
        sortByNumberProperty(a, b, "edit_target_price", sortOrder)
      )[0];
    if (orderToUpdate) {
      const orderIndexToUpdate = updatedOrders.findIndex(
        (order) =>
          order.status === OrderStatus.PENDING &&
          order.edit_target_price === orderToUpdate.edit_target_price
      );
      const updatedOrderAmount = addFloats(
        orderToUpdate.edit_amount,
        leftoverAmount
      );
      updatedOrders[orderIndexToUpdate] = {
        ...orderToUpdate,
        edit_amount: toFixed(updatedOrderAmount, amountPrecision),
        edit_quantity: toFixed(
          (updatedOrderAmount / orderToUpdate.edit_amount) *
            orderToUpdate.edit_quantity,
          quantityPrecision
        ),
      };
    }
  }
  return updatedOrders;
};

export const getTradeAmount = (entryOrders: EditOrderData[]) =>
  entryOrders
    .filter((order) => order.status !== OrderStatus.CANCELLED)
    .reduce((sum, order) => addFloats(sum, order.edit_amount), 0);

export const getEntryEditOrdersWithUpdatedRatios = (
  entryOrders: EditOrderData[],
  ratioPrecision: number,
  tradeAmount?: number
): EditOrderData[] => {
  let _tradeAmount = tradeAmount || getTradeAmount(entryOrders);
  return entryOrders.map((order) => ({
    ...order,
    edit_ratio: toFixed(
      order.edit_amount / (_tradeAmount || 1),
      ratioPrecision
    ),
  }));
};

export const getFilledTradeAmount = (
  entryOrders: Pick<OrderCardData, "filled_amount" | "target_price">[]
) => entryOrders.reduce((sum, order) => sum + order.filled_amount, 0);

export const getWeightedAverageEditOrderPrice = (
  orders: Pick<OrderCardData, "filled_amount" | "target_price">[]
) => {
  const fulfilledEntries = orders.filter((order) => order.filled_amount > 0);
  const fulfilledEntriesAmount = getFilledTradeAmount(orders);
  return fulfilledEntries.reduce((average, order) => {
    average +=
      (order.filled_amount / fulfilledEntriesAmount) * order.target_price;
    return average;
  }, 0);
};

const getExistingQuantityOrder = (
  quantity: number,
  amount: number,
  price: number
): EditOrderData => {
  const newExistingQuantityOrder = getDefaultEditOrder();
  newExistingQuantityOrder.quantity = quantity;
  newExistingQuantityOrder.edit_quantity = quantity;
  newExistingQuantityOrder.amount = amount;
  newExistingQuantityOrder.edit_amount = amount;
  newExistingQuantityOrder.target_price = price;
  newExistingQuantityOrder.edit_target_price = price;
  newExistingQuantityOrder.status = OrderStatus.FULFILLED;
  newExistingQuantityOrder.reason = "Existing Quantity";
  return newExistingQuantityOrder;
};

const mergeEditOrders = (
  firstOrder: EditOrderData,
  secondOrder: EditOrderData
): EditOrderData => {
  const mergedOrder = { ...firstOrder };
  mergedOrder.edit_ratio = addFloats(
    mergedOrder.edit_ratio,
    secondOrder.edit_ratio
  );
  mergedOrder.edit_quantity = addFloats(
    mergedOrder.edit_quantity,
    secondOrder.edit_quantity
  );
  mergedOrder.edit_amount = addFloats(
    mergedOrder.edit_amount,
    secondOrder.edit_amount
  );
  return mergedOrder;
};

export const updateEntryOrdersWithAddedQuantity = (
  entryOrders: EditOrderData[],
  addedQuantity: number,
  amountPrecision: number,
  _quantityPrecision: number,
  currentPrice: number,
  leverage: number,
  conversionFormula?: string
): EditOrderData[] => {
  let quantityAmount =
    conversionFormula &&
    getNerdamerConversionFormulaValue(
      { quantity: (addedQuantity || 0).toString() },
      leverage,
      currentPrice,
      "amount",
      conversionFormula
    );
  quantityAmount = quantityAmount
    ? toFixed(quantityAmount, amountPrecision || 0)
    : 0;
  const previousExistingQuantityOrderIndex = entryOrders.findIndex(
    (order) =>
      order.status === OrderStatus.FULFILLED &&
      order.reason.toLowerCase() === "existing quantity" &&
      order.target_price === currentPrice
  );
  const newExistingQuantityOrder = getExistingQuantityOrder(
    addedQuantity,
    quantityAmount,
    currentPrice
  );
  let updatedEntryOrders = [...entryOrders];
  if (previousExistingQuantityOrderIndex !== -1) {
    const previousExistingQuantityOrder =
      updatedEntryOrders[previousExistingQuantityOrderIndex];
    updatedEntryOrders[previousExistingQuantityOrderIndex] = mergeEditOrders(
      newExistingQuantityOrder,
      previousExistingQuantityOrder
    );
  } else {
    updatedEntryOrders.unshift(newExistingQuantityOrder);
  }
  return updatedEntryOrders;
};

export const getDefaultOrderData = (): Omit<OrderCardData, "id"> => {
  return {
    number: -1,
    status: OrderStatus.PENDING,
    reason: "",
    quantity: 0,
    filled_quantity: 0,
    amount: 0,
    filled_amount: 0,
    target_price: 0,
    ratio: 0,
    display_ratio: 0,
    ratio_filled: 0,
  };
};

export const getDefaultEditOrder = (): EditOrderData => {
  return {
    edit_target_price: 0,
    edit_amount: 0,
    edit_ratio: 0,
    edit_quantity: 0,
    ...getDefaultOrderData(),
  };
};

export const validateSaveOrder = (
  isSignal: boolean,
  errors: OrdersErrors,
  valuesModified: boolean,
  openMessage: OpenMessage
) => {
  const numErrors = Object.values(errors).reduce((acc, targetSectionErrors) => {
    acc += Object.keys(targetSectionErrors.section).length;
    return acc;
  }, 0);
  const signalText = isSignal ? "signal" : "trade";
  let errorMsg = "";
  if (numErrors > 0) {
    const atLeastOneEntryError = Object.keys(errors[OrderTypes.ENTRY]).includes(
      ErrorTypes.AT_LEAST_ONE_ENTRY.toString()
    );
    if (atLeastOneEntryError) {
      errorMsg = `Saving a ${signalText} without any entries will immediately cancel the signal. If you wish to close the signal, exit the edit mode and click on the trash icon.`;
    }
    errorMsg =
      "You need to resolve the remaining issues before saving your changes";
  } else if (!valuesModified) {
    errorMsg = `You did not modify any of the ${signalText} values`;
  }
  if (errorMsg) {
    openMessage(errorMsg, "warning");
    return false;
  }
  return true;
};

export const validateEditTradeOrders = (
  ordersData: ValidationOrdersData,
  isShort: boolean,
  isBreakout: boolean,
  currentPrice: number,
  minPrice: number,
  maxPrice: number,
  amountData: TradeAmountData,
  quantityData: TradeAmountData
): OrdersErrors => {
  const errors = Object.entries(ordersData).reduce(
    (acc, [orderType, ordersData]) => {
      acc[orderType as OrderTypes] = getDefaultOrdersSectionErrors(ordersData);
      return acc;
    },
    {} as OrdersErrors
  );

  validateOrdersPrices(
    ordersData,
    errors,
    isShort,
    isBreakout,
    currentPrice,
    minPrice,
    maxPrice,
    false,
    false
  );

  validateNumberOfOrders(
    ordersData[OrderTypes.ENTRY],
    errors,
    isBreakout,
    amountData.min || 0,
    quantityData.selected,
    quantityData.min || 0
  );

  const takeProfitsPercentSum =
    ordersData[OrderTypes.TAKE_PROFIT].reduce(
      (sum, order) => addFloats(sum, toFixed(order.percent, 2)),
      0
    ) > 100;
  if (takeProfitsPercentSum) {
    addError(
      errors[OrderTypes.TAKE_PROFIT],
      ErrorTypes.EDIT_TRADE_SUM_OF_RATIOS
    );
  }

  return errors;
};

export const getExtraOrderData = (
  order: EditOrderData,
  weightedAverageEntryPrice: number | undefined,
  percentPrecision: number
) => {
  let orderExtraData = order.order_extra_data
    ? [...order.order_extra_data]
    : [];
  const potentialProfitIndex = orderExtraData?.findIndex(
    (extraData) => extraData[0].toLowerCase() === "potential profit"
  );

  const getDistanceExtraData = (): [string, string] => {
    let distance: string;
    if (weightedAverageEntryPrice) {
      distance =
        toFixed(
          (Math.abs(order.edit_target_price - weightedAverageEntryPrice) /
            weightedAverageEntryPrice) *
            100,
          percentPrecision
        ).toString() + "%";
    } else {
      distance = "N/A";
    }
    return ["Distance", distance];
  };

  if (
    orderExtraData &&
    potentialProfitIndex !== undefined &&
    potentialProfitIndex > -1
  ) {
    orderExtraData[potentialProfitIndex] = getDistanceExtraData();
  } else if (!orderExtraData.length) {
    orderExtraData = [getDistanceExtraData()];
  }
  return orderExtraData;
};

export const getSellExistingCoinWarning = (isBot?: boolean) => {
  const objectName = isBot ? "bot" : "trade";
  return `The 'Sell coins at current price' option is disabled because the selected ${objectName}'s symbol is currently inactive in the exchange.`;
};
